import React, { useCallback, useEffect, useState } from 'react';
import { reportsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { fmtTime } from '../../utils/dateUtils';
import { fmtDuration } from '../../utils/duration';
import { parseApiError } from '../../utils/errors';

function monthStartISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default function MyTimesheet() {
  const { user } = useAuth();
  const [fromDate, setFromDate] = useState(monthStartISO());
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    if (!user?.id) return;
    setLoading(true);
    reportsAPI.timesheet(user.id, { from_date: fromDate, to_date: toDate, tz_offset: -new Date().getTimezoneOffset() })
      .then(({ data }) => setData(data))
      .catch((e) => setError(parseApiError(e, 'Failed to load timesheet')))
      .finally(() => setLoading(false));
  }, [user, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    if (!user?.id) return;
    setDownloading(true);
    try {
      const { data: blob } = await reportsAPI.downloadMonthly(user.id, {
        from_date: fromDate, to_date: toDate, tz_offset: -new Date().getTimezoneOffset(),
      });
      const url = URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url; a.download = `my_timesheet_${fromDate}_to_${toDate}.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  };

  const s = data?.summary;

  return (
    <div className="page">
      <div className="page-header"><h2>My Timesheet</h2></div>

      <div className="filter-bar mb-4">
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>From
          <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} style={{ marginLeft: 4 }} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>To
          <input type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} style={{ marginLeft: 4 }} />
        </label>
        <button className="btn btn-primary" onClick={download} disabled={downloading}>{downloading ? '…' : '⬇ Download CSV'}</button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {s && (
        <div className="stats-grid mb-4">
          <div className="stat-card stat-card-blue"><div className="stat-icon">🏢</div><div className="stat-body"><div className="stat-value">{fmtDuration(s.office_hours)}</div><div className="stat-title">Office Time (in–out)</div></div></div>
          <div className="stat-card stat-card-orange"><div className="stat-icon">☕</div><div className="stat-body"><div className="stat-value">{fmtDuration(s.break_hours)}</div><div className="stat-title">Break Time</div></div></div>
          <div className="stat-card stat-card-green"><div className="stat-icon">💼</div><div className="stat-body"><div className="stat-value">{fmtDuration(s.worked_hours)}</div><div className="stat-title">Worked (excl. break)</div></div></div>
          <div className="stat-card stat-card-purple"><div className="stat-icon">✅</div><div className="stat-body"><div className="stat-value">{fmtDuration(s.task_hours)}</div><div className="stat-title">Task Time</div></div></div>
          <div className="stat-card stat-card-teal"><div className="stat-icon">📅</div><div className="stat-body"><div className="stat-value">{s.leave_days}</div><div className="stat-title">Leave Days</div></div></div>
        </div>
      )}

      {loading ? <div className="page-loading">Loading…</div> : (
        <div className="card">
          <div className="card-header"><h3>Daily Breakdown</h3></div>
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Day</th><th>Punch In</th><th>Punch Out</th><th>Break</th><th>Worked</th><th>Task Time</th></tr>
            </thead>
            <tbody>
              {(!data || data.days.length === 0) ? (
                <tr><td colSpan={7} className="text-center text-muted">No records for this period</td></tr>
              ) : data.days.map((d) => (
                <tr key={d.date}>
                  <td style={{ whiteSpace: 'nowrap' }}>{d.date}</td>
                  <td>{d.day}</td>
                  <td>{d.punch_in ? fmtTime(d.punch_in) : '—'}</td>
                  <td>{d.punch_out ? fmtTime(d.punch_out) : '—'}</td>
                  <td>{d.break_hours ? fmtDuration(d.break_hours) : '—'}</td>
                  <td>{d.worked_hours ? fmtDuration(d.worked_hours) : '—'}</td>
                  <td>{d.task_hours ? fmtDuration(d.task_hours) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
